import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TruckIcon, ShieldCheckIcon, ClockIcon, ChevronDownIcon, CalendarIcon, PhoneIcon } from 'lucide-react';
import { RentalFormData } from '../App';
interface RentalFormSectionProps {
  onSubmitForm: (data: RentalFormData) => void;
}
export function RentalFormSection({
  onSubmitForm
}: RentalFormSectionProps) {
  const [formData, setFormData] = useState({
    rentalType: 'Residential',
    placeName: '',
    placeAddress: '',
    deliveryLocation: '',
    equipment: '',
    rentalDate: '',
    returnDate: '',
    weight: '',
    height: '',
    agreeMinimum: false,
    agreeInsurance: false
  });
  const rentalTypes = ['Residential', 'Hotel', 'Airbnb', 'Baltimore Ports', 'Seaports', 'Others'];
  const deliveryLocations = ['Washington DC', 'Maryland', 'Baltimore', 'Northern Virginia'];
  const equipmentOptions = ['Rollator Walker', 'Power Wheelchair', 'Knee Walker', 'Manual Wheelchair', '3 or 4 Wheel Scooter (Regular)', '3 or 4 Wheel Scooter (Heavy Duty)', 'Heavy Duty Manual Wheelchair', 'Baby Stroller'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const {
      agreeMinimum,
      agreeInsurance,
      ...rentalData
    } = formData;
    onSubmitForm(rentalData);
  };
  return <section id="reserve-form" className="py-24 bg-brand-gray relative overflow-hidden" data-id="element-332">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-333">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-navy/5 rounded-full blur-3xl" data-id="element-334"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-red/5 rounded-full blur-3xl" data-id="element-335"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-336">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-337">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="flex items-center justify-center gap-3 mb-4" data-id="element-338">
            <div className="h-[2px] w-8 bg-brand-red" data-id="element-339"></div>
            <span className="text-brand-red font-bold tracking-wider text-sm uppercase" data-id="element-340">
              Online Reservation
            </span>
            <div className="h-[2px] w-8 bg-brand-red" data-id="element-341"></div>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-brand-navy mb-6" data-id="element-342">
            Reserve Your Equipment
          </motion.h2>
        </div>

        {/* Main Card */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} className="bg-brand-navy rounded-[2rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row" data-id="element-343">
          {/* Left Column: Form (~60%) */}
          <div className="w-full lg:w-[60%] p-8 md:p-12 lg:p-16" data-id="element-344">
            <form onSubmit={handleSubmit} className="space-y-8" data-id="element-345">
              {/* Rental Type Pills */}
              <div data-id="element-346">
                <label className="block text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider" data-id="element-347">
                  Rental Type
                </label>
                <div className="flex overflow-x-auto pb-2 -mx-2 px-2 sm:mx-0 sm:px-0 sm:pb-0 sm:flex-wrap gap-3 scrollbar-hide" data-id="element-348">
                  {rentalTypes.map(type => <button key={type} type="button" onClick={() => setFormData({
                  ...formData,
                  rentalType: type
                })} className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${formData.rentalType === type ? 'bg-brand-red text-white shadow-lg shadow-brand-red/20' : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'}`} data-id="element-349">
                      {type}
                    </button>)}
                </div>
              </div>

              {/* Place Name & Address */}
              <div className="space-y-6" data-id="element-350">
                <div data-id="element-351">
                  <label htmlFor="placeName" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-352">
                    Place Name
                  </label>
                  <input type="text" id="placeName" name="placeName" value={formData.placeName} onChange={handleInputChange} placeholder="Enter place name" required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none" data-id="element-353" />
                </div>
                <div data-id="element-354">
                  <label htmlFor="placeAddress" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-355">
                    Place Address
                  </label>
                  <input type="text" id="placeAddress" name="placeAddress" value={formData.placeAddress} onChange={handleInputChange} placeholder="Enter place address" required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none" data-id="element-356" />
                </div>
              </div>

              {/* Dropdowns */}
              <div className="space-y-6" data-id="element-357">
                <div className="relative" data-id="element-358">
                  <label htmlFor="deliveryLocation" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-359">
                    Delivery Location
                  </label>
                  <div className="relative" data-id="element-360">
                    <select id="deliveryLocation" name="deliveryLocation" value={formData.deliveryLocation} onChange={handleInputChange} required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none appearance-none" data-id="element-361">
                      <option value="" disabled className="text-gray-900" data-id="element-362">
                        Select One
                      </option>
                      {deliveryLocations.map(loc => <option key={loc} value={loc} className="text-gray-900" data-id="element-363">
                          {loc}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-364" />
                  </div>
                </div>
                <div className="relative" data-id="element-365">
                  <label htmlFor="equipment" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-366">
                    Equipment
                  </label>
                  <div className="relative" data-id="element-367">
                    <select id="equipment" name="equipment" value={formData.equipment} onChange={handleInputChange} required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none appearance-none" data-id="element-368">
                      <option value="" disabled className="text-gray-900" data-id="element-369">
                        Select One
                      </option>
                      {equipmentOptions.map(eq => <option key={eq} value={eq} className="text-gray-900" data-id="element-370">
                          {eq}
                        </option>)}
                    </select>
                    <ChevronDownIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-371" />
                  </div>
                </div>
              </div>

              {/* Dates Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-372">
                <div data-id="element-373">
                  <label htmlFor="rentalDate" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-374">
                    Rental Date
                  </label>
                  <div className="relative" data-id="element-375">
                    <input type="date" id="rentalDate" name="rentalDate" value={formData.rentalDate} onChange={handleInputChange} required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" data-id="element-376" />
                    <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-377" />
                  </div>
                </div>
                <div data-id="element-378">
                  <label htmlFor="returnDate" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-379">
                    Return Date
                  </label>
                  <div className="relative" data-id="element-380">
                    <input type="date" id="returnDate" name="returnDate" value={formData.returnDate} onChange={handleInputChange} required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer" data-id="element-381" />
                    <CalendarIcon className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50 pointer-events-none" data-id="element-382" />
                  </div>
                </div>
              </div>

              {/* Weight & Height Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-383">
                <div data-id="element-384">
                  <label htmlFor="weight" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-385">
                    Weight
                  </label>
                  <input type="text" id="weight" name="weight" value={formData.weight} onChange={handleInputChange} placeholder="Weight in Pounds" required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none" data-id="element-386" />
                </div>
                <div data-id="element-387">
                  <label htmlFor="height" className="block text-white/80 text-sm font-semibold mb-2 uppercase tracking-wider" data-id="element-388">
                    Height
                  </label>
                  <input type="text" id="height" name="height" value={formData.height} onChange={handleInputChange} placeholder="Height in ft/cm" required className="w-full bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl py-3.5 px-4 focus:border-brand-red focus:ring-1 focus:ring-brand-red/50 transition-all outline-none" data-id="element-389" />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="space-y-4 pt-2" data-id="element-390">
                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-391">
                  <div className="relative flex items-center justify-center mt-0.5" data-id="element-392">
                    <input type="checkbox" name="agreeMinimum" checked={formData.agreeMinimum} onChange={handleCheckboxChange} required className="peer appearance-none w-5 h-5 border-2 border-white/30 rounded bg-white/5 checked:bg-brand-red checked:border-brand-red transition-all outline-none focus:ring-2 focus:ring-brand-red/50 focus:ring-offset-1 focus:ring-offset-brand-navy" data-id="element-393" />
                    <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-394">
                      <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-395" />
                    </svg>
                  </div>
                  <span className="text-white/80 text-sm leading-relaxed group-hover:text-white transition-colors" data-id="element-396">
                    3 days minimum for all rentals. You can call for more
                    information or details.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer group" data-id="element-397">
                  <div className="relative flex items-center justify-center mt-0.5" data-id="element-398">
                    <input type="checkbox" name="agreeInsurance" checked={formData.agreeInsurance} onChange={handleCheckboxChange} required className="peer appearance-none w-5 h-5 border-2 border-white/30 rounded bg-white/5 checked:bg-brand-red checked:border-brand-red transition-all outline-none focus:ring-2 focus:ring-brand-red/50 focus:ring-offset-1 focus:ring-offset-brand-navy" data-id="element-399" />
                    <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-400">
                      <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-401" />
                    </svg>
                  </div>
                  <span className="text-white/80 text-sm leading-relaxed group-hover:text-white transition-colors" data-id="element-402">
                    Insurance of $10 fee applies.
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-4" data-id="element-403">
                <button type="submit" className="w-full py-4 bg-brand-red text-white rounded-xl font-bold text-lg hover:bg-red-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 outline-none focus:ring-2 focus:ring-brand-red focus:ring-offset-2 focus:ring-offset-brand-navy" data-id="element-404">
                  Find Rent Rates
                </button>
              </div>
            </form>
          </div>

          {/* Right Column: Info Panel (~40%) */}
          <div className="w-full lg:w-[40%] bg-gradient-to-br from-[#0a1d3d] to-[#051124] p-8 md:p-12 lg:p-16 flex flex-col justify-between relative overflow-hidden" data-id="element-405">
            {/* Decorative circles */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-brand-blue/10 rounded-full blur-3xl" data-id="element-406"></div>
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-brand-red/10 rounded-full blur-3xl" data-id="element-407"></div>

            <div className="relative z-10" data-id="element-408">
              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-409">
                Online Reservation
              </h3>
              <p className="text-white/70 text-lg leading-relaxed mb-12" data-id="element-410">
                Get your mobility equipment delivered to your door. Best rates
                guaranteed.
              </p>

              <div className="space-y-8" data-id="element-411">
                <div className="flex items-start gap-4" data-id="element-412">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 border border-white/10" data-id="element-413">
                    <TruckIcon className="w-6 h-6 text-brand-red" data-id="element-414" />
                  </div>
                  <div data-id="element-415">
                    <h4 className="text-white font-bold text-lg mb-1" data-id="element-416">
                      Free Delivery
                    </h4>
                    <p className="text-white/60 text-sm" data-id="element-417">
                      To your hotel, residence, or port
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-418">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 border border-white/10" data-id="element-419">
                    <ShieldCheckIcon className="w-6 h-6 text-brand-red" data-id="element-420" />
                  </div>
                  <div data-id="element-421">
                    <h4 className="text-white font-bold text-lg mb-1" data-id="element-422">
                      Best Rate Guarantee
                    </h4>
                    <p className="text-white/60 text-sm" data-id="element-423">
                      We beat any advertised estimate
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-424">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center shrink-0 border border-white/10" data-id="element-425">
                    <ClockIcon className="w-6 h-6 text-brand-red" data-id="element-426" />
                  </div>
                  <div data-id="element-427">
                    <h4 className="text-white font-bold text-lg mb-1" data-id="element-428">
                      24/7 Support
                    </h4>
                    <p className="text-white/60 text-sm" data-id="element-429">
                      We're always here when you need us
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-16 pt-8 border-t border-white/10" data-id="element-430">
              <p className="text-white/50 text-sm font-medium uppercase tracking-wider mb-3" data-id="element-431">
                Need immediate assistance?
              </p>
              <a href="tel:+18003954459" className="inline-flex items-center gap-3 text-2xl font-extrabold text-white hover:text-brand-red transition-colors group" data-id="element-432">
                <PhoneIcon className="w-6 h-6 group-hover:animate-bounce" data-id="element-433" />
                1(800)-395-4459
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}